import { BookOpenCheck, LoaderCircle, Sparkles } from "lucide-react";
import type { AiNameAnalysisState, NameAnalysis } from "./api";
import "./name-analysis.css";

export function NameAnalysisEnhancement({
  analysis,
  ai,
  language,
}: {
  analysis: NameAnalysis;
  ai: AiNameAnalysisState | null;
  language: "zh" | "en";
}) {
  const famous = analysis.famousCase;
  return (
    <div className={`name-analysis-enhancement ${language}`}>
      {famous ? (
        <article className="famous-name-case">
          <span>
            <BookOpenCheck />
            {language === "zh" ? "历史文化同名案例" : "Historical same-name case"}
          </span>
          <h3>{famous.fullName}</h3>
          <p>
            {language === "zh"
              ? `${famous.eraZh} · ${famous.roleZh}。${famous.knownForZh}。`
              : `${famous.eraEn} · ${famous.roleEn}. ${famous.knownForEn}.`}
          </p>
          <a href={famous.referenceUrl} target="_blank" rel="noreferrer">
            {language === "zh" ? "查看公共资料来源" : "View public reference"}
          </a>
          <small>
            {language === "zh"
              ? "同名人物仅作文化背景参考，不代表姓名来源、人格或命运。"
              : "This is cultural context only, not evidence of name origin, personality, or destiny."}
          </small>
        </article>
      ) : null}

      {ai?.status === "signin_required" ? (
        <article className="ai-analysis-note">
          <Sparkles />
          <p>
            {language === "zh"
              ? "登录后可免费获得 DeepSeek 深度解析，每天最多 5 个未缓存姓名。"
              : "Sign in for a free DeepSeek analysis, up to five uncached names per day."}
          </p>
        </article>
      ) : null}

      {ai && ["queued", "processing", "retrying"].includes(ai.status) ? (
        <article className="ai-analysis-note loading">
          <LoaderCircle />
          <p>
            {language === "zh"
              ? "DeepSeek 正在基于已核验字义和文化事实进行解析…"
              : "DeepSeek is analysing the verified meanings and cultural facts…"}
          </p>
        </article>
      ) : null}

      {ai?.status === "limit_reached" ? (
        <article className="ai-analysis-note">
          <p>
            {ai.message ??
              (language === "zh"
                ? "今天的 AI 深度解析次数已用完。"
                : "Today's AI analysis allowance has been used.")}
          </p>
        </article>
      ) : null}

      {ai?.status === "failed" ? (
        <article className="ai-analysis-note">
          <p>
            {language === "zh"
              ? "AI 深度解析暂时失败；上方本地字义和风险结果仍然有效。"
              : "AI analysis is temporarily unavailable; the local meanings and watch-outs above remain valid."}
          </p>
        </article>
      ) : null}

      {ai?.status === "completed" && ai.result ? (
        <article className="ai-analysis-result">
          <span>
            <Sparkles /> DeepSeek · {ai.model ?? "AI"}
          </span>
          <h3>{language === "zh" ? "AI 深度解析" : "AI deep analysis"}</h3>
          <p>{ai.result.summary}</p>
          <dl>
            <div>
              <dt>{language === "zh" ? "字义关系" : "Character interaction"}</dt>
              <dd>{ai.result.characterInteraction}</dd>
            </div>
            <div>
              <dt>{language === "zh" ? "音形" : "Sound and form"}</dt>
              <dd>{ai.result.soundAndForm}</dd>
            </div>
            <div>
              <dt>{language === "zh" ? "实际使用" : "Real-life use"}</dt>
              <dd>{ai.result.usage}</dd>
            </div>
            <div>
              <dt>{language === "zh" ? "文化语境" : "Cultural context"}</dt>
              <dd>{ai.result.culturalContext}</dd>
            </div>
          </dl>
          {ai.result.cautions.length ? (
            <ul>
              {ai.result.cautions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
          <small>
            {ai.result.evidenceNote} · {language === "zh" ? "可信度" : "Confidence"}:{" "}
            {ai.result.confidence}
          </small>
        </article>
      ) : null}
    </div>
  );
}
